// <luna-carousel>: scroll-snap track with previous/next buttons, dot navigation, mouse dragging and optional autoplay.
// Markup: [data-track] holding [data-slide] children, [data-prev], [data-next], [data-dots], [data-toggle].
// Autoplay: data-autoplay="step" (one slide every data-interval seconds) or "continuous" (data-speed px per second).
// Touch swiping uses the browser's own scrolling; mouse dragging is handled here.
if (!customElements.get('luna-carousel')) {
  class LunaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.dotsContainer = this.querySelector('[data-dots]');
      this.toggleButton = this.querySelector('[data-toggle]');
      if (!this.track || this.slides.length === 0) return;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mode = this.slides.length > 1 && !this.reduceMotion ? this.dataset.autoplay || 'off' : 'off';
      this.interval = Math.max(2, parseFloat(this.dataset.interval) || 5) * 1000;
      this.speed = Math.max(5, parseFloat(this.dataset.speed) || 30);
      this.allSlides = this.slides;
      this.pauses = new Set();
      this.position = 0;

      if (this.mode === 'continuous') this.addClones();
      this.buildDots();

      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));
      this.toggleButton?.addEventListener('click', () => this.setUserPaused(!this.pauses.has('user')));

      this.onScroll = () => {
        // A scroll that does not match the autoplay position came from the visitor; continue from there.
        if (this.mode === 'continuous' && Math.abs(this.track.scrollLeft - this.position) > 2) {
          this.position = this.track.scrollLeft;
        }
        this.update();
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.setupDragging();
      if (this.mode !== 'off') this.setupAutoplay();
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.observer?.disconnect();
      clearInterval(this.timer);
      cancelAnimationFrame(this.frame);
      this.stopAnimation();
    }

    // Copies of the slides after the originals make the continuous scroll loop seamlessly.
    addClones() {
      this.track.classList.add('is-continuous');
      const clones = this.slides.map((slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-slide');
        clone.removeAttribute('id');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        this.track.appendChild(clone);
        return clone;
      });
      this.allSlides = this.slides.concat(clones);
    }

    loopWidth() {
      return this.allSlides[this.slides.length].offsetLeft - this.slides[0].offsetLeft;
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    // Scroll position that puts a slide at the start of the track (after the scroll padding).
    slideLeft(slide) {
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      return slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - padding;
    }

    // Mouse dragging. Touch keeps the native swipe, which already scrolls the track.
    setupDragging() {
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.stopAnimation();
        this.drag = { id: event.pointerId, startX: event.clientX, startLeft: this.track.scrollLeft, startIndex: this.currentIndex(), lastX: event.clientX, lastTime: event.timeStamp, velocity: 0, moved: false };
        this.pause('drag');
      });
      this.track.addEventListener('pointermove', (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.startX;
        if (!drag.moved && Math.abs(dx) > 5) {
          drag.moved = true;
          this.track.setPointerCapture(drag.id);
          this.track.classList.add('is-dragging');
        }
        if (!drag.moved) return;
        const elapsed = Math.max(1, event.timeStamp - drag.lastTime);
        drag.velocity = (event.clientX - drag.lastX) / elapsed;
        drag.lastX = event.clientX;
        drag.lastTime = event.timeStamp;
        this.track.scrollLeft = drag.startLeft - dx;
      });
      const end = (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        this.drag = null;
        this.track.classList.remove('is-dragging');
        this.resumeLater('drag', 3000);
        if (!drag.moved) return;
        // A dragged release must not also count as a click on something inside the slide.
        this.track.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, { capture: true, once: true });
        if (this.mode === 'continuous') {
          this.position = this.track.scrollLeft;
          return;
        }
        let index = this.currentIndex();
        // A quick flick moves at least one slide in the direction of the drag.
        if (index === drag.startIndex && Math.abs(drag.velocity) > 0.3) index += drag.velocity < 0 ? 1 : -1;
        this.goTo(Math.max(0, Math.min(index, this.slides.length - 1)));
        if (this.mode === 'step') this.restartTimer();
      };
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);
    }

    setupAutoplay() {
      if (this.toggleButton) this.toggleButton.hidden = false;

      // Pause while the pointer or keyboard focus is on the carousel, while touching, and when it is out of view.
      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.pause('hover'));
      this.addEventListener('pointerleave', () => this.resume('hover'));
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => !this.contains(event.relatedTarget) && this.resume('focus'));
      this.track.addEventListener('touchstart', () => this.pause('touch'), { passive: true });
      this.track.addEventListener('touchend', () => this.resumeLater('touch', 3000), { passive: true });
      this.track.addEventListener('wheel', () => {
        this.pause('touch');
        this.resumeLater('touch', 3000);
      }, { passive: true });

      this.onVisibility = () => (document.hidden ? this.pause('hidden') : this.resume('hidden'));
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observer = new IntersectionObserver(([entry]) =>
        entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')
      );
      this.observer.observe(this);

      if (this.mode === 'step') this.restartTimer();
      if (this.mode === 'continuous') {
        this.lastTime = null;
        this.frame = requestAnimationFrame((time) => this.tick(time));
      }
    }

    isRunning() {
      return this.mode !== 'off' && this.pauses.size === 0 && !this.animation;
    }

    pause(reason) {
      clearTimeout(this.resumeTimers?.[reason]);
      this.pauses.add(reason);
    }

    resume(reason) {
      this.pauses.delete(reason);
      this.lastTime = null;
    }

    resumeLater(reason, delay) {
      this.resumeTimers = this.resumeTimers || {};
      clearTimeout(this.resumeTimers[reason]);
      this.resumeTimers[reason] = setTimeout(() => this.resume(reason), delay);
    }

    setUserPaused(paused) {
      if (paused) this.pause('user');
      else this.resume('user');
      this.classList.toggle('is-paused', paused);
      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-pressed', String(paused));
        this.toggleButton.setAttribute(
          'aria-label',
          paused ? this.toggleButton.dataset.playLabel : this.toggleButton.dataset.pauseLabel
        );
      }
    }

    restartTimer() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (this.isRunning()) this.step(1, true);
      }, this.interval);
    }

    tick(time) {
      if (this.lastTime !== null && this.isRunning()) {
        const elapsed = Math.min(time - this.lastTime, 100);
        const loop = this.loopWidth();
        this.position += (this.speed * elapsed) / 1000;
        if (loop > 0 && this.position >= loop) this.position -= loop;
        this.track.scrollLeft = this.position;
      }
      this.lastTime = time;
      this.frame = requestAnimationFrame((next) => this.tick(next));
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.dots = this.slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'luna-carousel__dot';
        dot.setAttribute('aria-label', `${this.dataset.slideLabel || 'Slide'} ${index + 1}`);
        dot.addEventListener('click', () => {
          this.goTo(index);
          if (this.mode === 'step') this.restartTimer();
        });
        this.dotsContainer.appendChild(dot);
        return dot;
      });
    }

    currentIndex() {
      const start = this.track.getBoundingClientRect().left;
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      let closest = 0;
      let distance = Infinity;
      this.allSlides.forEach((slide, index) => {
        const offset = Math.abs(slide.getBoundingClientRect().left - start - padding);
        if (offset < distance) {
          distance = offset;
          closest = index;
        }
      });
      return closest % this.slides.length;
    }

    atStart() {
      return this.track.scrollLeft <= 4;
    }

    atEnd() {
      return this.track.scrollLeft >= this.maxScroll() - 4;
    }

    // One slide forward or back. At the ends it wraps around, because the last slides can't scroll to the start.
    step(direction, automatic = false) {
      if (this.mode === 'continuous') {
        const width = this.slides[1] ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : this.track.clientWidth;
        let left = this.track.scrollLeft + direction * width;
        const loop = this.loopWidth();
        if (left < 0) left += loop;
        this.animateTo(left);
        return;
      }
      if (direction > 0 && this.atEnd()) this.animateTo(0);
      else if (direction < 0 && this.atStart()) this.animateTo(this.maxScroll());
      else this.goTo(this.currentIndex() + direction);
      if (this.mode === 'step' && !automatic) this.restartTimer();
    }

    goTo(index) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      this.animateTo(this.slideLeft(slide));
    }

    // Animates scrollLeft directly, with snapping switched off meanwhile, so it behaves the same in every browser.
    animateTo(target) {
      this.stopAnimation();
      const track = this.track;
      const max = this.mode === 'continuous' ? Infinity : this.maxScroll();
      const to = Math.max(0, Math.min(target, max));
      const from = track.scrollLeft;
      const distance = to - from;
      if (Math.abs(distance) < 1 || this.reduceMotion) {
        track.scrollLeft = to;
        this.finishAnimation();
        return;
      }
      const duration = Math.min(700, 300 + Math.abs(distance) * 0.35);
      const start = performance.now();
      track.classList.add('is-animating');
      this.animation = { done: false };
      const frame = (now) => {
        if (!this.animation) return;
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        track.scrollLeft = from + distance * eased;
        if (this.mode === 'continuous') this.position = track.scrollLeft;
        if (progress < 1) this.schedule(frame);
        else this.finishAnimation();
      };
      this.schedule(frame);
    }

    // requestAnimationFrame, with a timer fallback for tabs or previews where frames are paused.
    schedule(callback) {
      const animation = this.animation;
      let ran = false;
      const run = (time) => {
        if (ran || this.animation !== animation) return;
        ran = true;
        callback(time);
      };
      animation.raf = requestAnimationFrame(run);
      animation.timeout = setTimeout(() => run(performance.now()), 34);
    }

    stopAnimation() {
      if (!this.animation) return;
      cancelAnimationFrame(this.animation.raf);
      clearTimeout(this.animation.timeout);
      this.animation = null;
      this.track?.classList.remove('is-animating');
    }

    finishAnimation() {
      this.stopAnimation();
      if (this.mode === 'continuous') {
        const loop = this.loopWidth();
        if (loop > 0 && this.track.scrollLeft >= loop) this.track.scrollLeft -= loop;
        this.position = this.track.scrollLeft;
      }
      this.lastTime = null;
      this.update();
    }

    update() {
      const index = this.currentIndex();
      const atEnd = this.atEnd();
      this.dots?.forEach((dot, dotIndex) => {
        const active = atEnd && this.mode !== 'continuous' ? dotIndex === this.slides.length - 1 : dotIndex === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('luna-carousel', LunaCarousel);
}
